// Circle of Fifths — 3 concentric rings (major / minor / diminished).
// Clicking a wedge invokes onPick({ rootName, quality }).
import React from "react";

// Clockwise order starting at 12 o'clock
const MAJ = ["C", "G", "D", "A", "E", "B", "Gb", "Db", "Ab", "Eb", "Bb", "F"];
const MIN = ["Am", "Em", "Bm", "F#m", "C#m", "G#m", "Ebm", "Bbm", "Fm", "Cm", "Gm", "Dm"];
const DIM = ["B°", "F#°", "C#°", "G#°", "D#°", "A#°", "F°", "C°", "G°", "D°", "A°", "E°"];

const RADII = { r1: 90, r2: 140, r3: 190, r4: 240 };

function polar(cx, cy, r, angleRad) {
  return [cx + r * Math.cos(angleRad), cy + r * Math.sin(angleRad)];
}

function wedgePath(cx, cy, rInner, rOuter, startAngle, endAngle) {
  const [x1, y1] = polar(cx, cy, rInner, startAngle);
  const [x2, y2] = polar(cx, cy, rOuter, startAngle);
  const [x3, y3] = polar(cx, cy, rOuter, endAngle);
  const [x4, y4] = polar(cx, cy, rInner, endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `L ${x2} ${y2}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x3} ${y3}`,
    `L ${x4} ${y4}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x1} ${y1}`,
    "Z",
  ].join(" ");
}

function labelForQuality(q) {
  return q === "maj" ? "" : q === "min" ? "m" : q === "dim" ? "°" : "";
}

// Parses "Am", "F#m", "B°", "Gb", "Ebm" into { rootName, quality }
function parse(name) {
  if (name.endsWith("°")) return { rootName: name.slice(0, -1), quality: "dim" };
  if (name.endsWith("m")) return { rootName: name.slice(0, -1), quality: "min" };
  return { rootName: name, quality: "maj" };
}

export default function CircleOfFifths({ onPick }) {
  const size = 520;
  const cx = size / 2;
  const cy = size / 2;
  const slice = (2 * Math.PI) / 12;
  const startOffset = -Math.PI / 2 - slice / 2; // C at the top

  const rings = [
    { r1: RADII.r3, r2: RADII.r4, names: MAJ, key: "maj" },
    { r1: RADII.r2, r2: RADII.r3, names: MIN, key: "min" },
    { r1: RADII.r1, r2: RADII.r2, names: DIM, key: "dim" },
  ];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      style={{ maxWidth: 520, display: "block" }}
      data-testid="circle-of-fifths-svg"
    >
      {rings.map((ring, ringIdx) =>
        ring.names.map((n, i) => {
          const startAngle = startOffset + i * slice;
          const endAngle = startAngle + slice;
          const midAngle = (startAngle + endAngle) / 2;
          const rMid = (ring.r1 + ring.r2) / 2;
          const [tx, ty] = polar(cx, cy, rMid, midAngle);
          const fill = `hsl(${(i * 30) % 360}, 65%, ${
            ringIdx === 0 ? 80 : ringIdx === 1 ? 72 : 64
          }%)`;
          const parsed = parse(n);
          return (
            <g key={`${ring.key}-${i}`}>
              <path
                data-testid={`circle-wedge-${ring.key}-${i}`}
                d={wedgePath(cx, cy, ring.r1, ring.r2, startAngle, endAngle)}
                fill={fill}
                stroke="#111827"
                strokeWidth={1.5}
                style={{ cursor: "pointer" }}
                onClick={() => onPick && onPick({ ...parsed, label: n })}
              />
              <text
                x={tx}
                y={ty}
                textAnchor="middle"
                dominantBaseline="middle"
                fontFamily="'Inter', sans-serif"
                fontSize={ringIdx === 0 ? 18 : ringIdx === 1 ? 15 : 13}
                fontWeight={600}
                fill="#111827"
                pointerEvents="none"
              >
                {parsed.rootName}
                {labelForQuality(parsed.quality) && (
                  <tspan fontSize={ringIdx === 0 ? 14 : 12} fontWeight={500}>
                    {labelForQuality(parsed.quality)}
                  </tspan>
                )}
              </text>
            </g>
          );
        })
      )}
      <circle cx={cx} cy={cy} r={RADII.r1 - 2} fill="#FFFFFF" stroke="#111827" strokeWidth={1.5} />
      <text
        x={cx}
        y={cy - 6}
        textAnchor="middle"
        fontFamily="'Inter', sans-serif"
        fontSize={13}
        fontWeight={600}
        fill="#111827"
      >
        Circle of
      </text>
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        fontFamily="'Inter', sans-serif"
        fontSize={13}
        fontWeight={600}
        fill="#111827"
      >
        Fifths
      </text>
    </svg>
  );
}
